import { useQuery } from "@tanstack/react-query";
import { getQueryFn } from "@/lib/queryClient";
import { useAuth } from "@/hooks/useAuth";
import { useLocation } from "wouter";
import { StatsCard } from "@/components/ui/stats-card";
import { StatusBadge } from "@/components/shared/status-badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { formatDate, formatCurrency, formatRelativeTime } from "@/lib/format";
import { Package, Ticket, Clock, FolderKanban, Receipt } from "lucide-react";
import type {
  Ticket as TicketType,
  ClientService,
  Product,
  Invoice,
  Project,
  ProjectTask,
} from "@db/schema";

interface DashboardData {
  activeServices: number;
  openTickets: number;
  pendingRequests: number;
  visibleProjects: number;
  visibleTasks: number;
  visibleInvoices: number;
  recentTickets: TicketType[];
  upcomingRenewals: Array<ClientService & { product: Product }>;
  recentTasks: Array<ProjectTask & { project: Project }>;
  recentInvoices: Invoice[];
}

export default function ClientDashboard() {
  const { user } = useAuth();
  const [, navigate] = useLocation();

  const { data, isLoading } = useQuery<DashboardData>({
    queryKey: ["/api/portal/dashboard"],
    queryFn: getQueryFn({ on401: "throw" }),
  });

  return (
    <div className="space-y-6">
      {/* Welcome header */}
      <div>
        <h2 className="text-2xl font-bold font-heading">
          Welcome{user?.firstName ? `, ${user.firstName}` : ""}
        </h2>
        <p className="text-muted-foreground">
          Manage your services and support requests
        </p>
      </div>

      {/* Stats cards */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
        {isLoading ? (
          <>
            <Skeleton className="h-[100px] rounded-lg" />
            <Skeleton className="h-[100px] rounded-lg" />
            <Skeleton className="h-[100px] rounded-lg" />
            <Skeleton className="h-[100px] rounded-lg" />
            <Skeleton className="h-[100px] rounded-lg" />
          </>
        ) : (
          <>
            <StatsCard
              title="Active Services"
              value={data?.activeServices ?? 0}
              icon={Package}
            />
            <StatsCard
              title="Open Tickets"
              value={data?.openTickets ?? 0}
              icon={Ticket}
            />
            <StatsCard
              title="Pending Requests"
              value={data?.pendingRequests ?? 0}
              icon={Clock}
            />
            <StatsCard
              title="Projects"
              value={data?.visibleProjects ?? 0}
              icon={FolderKanban}
            />
            <StatsCard
              title="Invoices"
              value={data?.visibleInvoices ?? 0}
              icon={Receipt}
            />
          </>
        )}
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Recent Tickets */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-lg">Recent Tickets</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="space-y-3">
                {Array.from({ length: 3 }).map((_, i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            ) : !data?.recentTickets?.length ? (
              <p className="text-sm text-muted-foreground py-4 text-center">
                No recent tickets
              </p>
            ) : (
              <div className="space-y-2">
                {data.recentTickets.map((ticket) => (
                  <div
                    key={ticket.id}
                    className="flex items-center justify-between rounded-md border p-3 cursor-pointer hover:bg-muted/50 transition-colors"
                    onClick={() => navigate(`/portal/tickets/${ticket.id}`)}
                  >
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium truncate">
                        {ticket.subject || "No subject"}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {formatRelativeTime(ticket.createdAt)}
                      </p>
                    </div>
                    <StatusBadge type="ticket" status={ticket.status} />
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Upcoming Renewals */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Upcoming Renewals</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="space-y-3">
                {Array.from({ length: 3 }).map((_, i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            ) : !data?.upcomingRenewals?.length ? (
              <p className="text-sm text-muted-foreground py-4 text-center">
                No upcoming renewals
              </p>
            ) : (
              <div className="space-y-2">
                {data.upcomingRenewals.map((service) => (
                  <div
                    key={service.id}
                    className="rounded-md border p-3 space-y-1"
                  >
                    <p className="text-sm font-medium truncate">
                      {service.customName || service.product.name}
                    </p>
                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      <span>{formatDate(service.nextRenewalDate)}</span>
                      <span className="font-medium text-foreground">
                        {formatCurrency(
                          service.price || service.product.defaultPrice,
                          service.product.currency,
                        )}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Visible Tasks</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="space-y-3">
                {Array.from({ length: 3 }).map((_, i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            ) : !data?.recentTasks?.length ? (
              <p className="text-sm text-muted-foreground py-4 text-center">
                No visible tasks yet
              </p>
            ) : (
              <div className="space-y-2">
                {data.recentTasks.map((task) => (
                  <div
                    key={task.id}
                    className="rounded-md border p-3 space-y-1"
                  >
                    <p className="text-sm font-medium">{task.title}</p>
                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      <span>{task.project.name}</span>
                      <span>{task.status.replace(/_/g, " ")}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Recent Invoices</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="space-y-3">
                {Array.from({ length: 3 }).map((_, i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            ) : !data?.recentInvoices?.length ? (
              <p className="text-sm text-muted-foreground py-4 text-center">
                No invoices available
              </p>
            ) : (
              <div className="space-y-2">
                {data.recentInvoices.map((invoice) => (
                  <div key={invoice.id} className="rounded-md border p-3 space-y-1">
                    <p className="text-sm font-medium">{invoice.reference}</p>
                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      <span>{formatDate(invoice.revenuePeriodEnd)}</span>
                      <span className="font-medium text-foreground">
                        {formatCurrency(invoice.totalAmount, invoice.currency)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
